import React, { useEffect, useState } from 'react'
import { Form, Input } from 'antd'
import './conversationTextFeedback.less'
import AppPrimaryButton from '../../UI/AppPrimaryButton/AppPrimaryButton'
import { CaretDownOutlined, CaretUpOutlined, DislikeOutlined, LikeOutlined } from '@ant-design/icons'
import { useEditConversationMutation } from '../../../store/api/pubic.api'
import { useTranslation } from 'react-i18next'
import { useGetConversationByIdQuery } from '../../../store/api/conversation.api'

type IProps = {
  conversationId: string
}

type Rating = 'like' | 'dislike'

const ConversationTextFeedback = ({ conversationId }: IProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const [whatIsWrong, setWhatIsWrong] = useState('')
  const [whyWrong, setWhyWrong] = useState('')
  const [correctResponse, setCorrectResponse] = useState('')
  const [rating, setRating] = useState<Rating | null>(null)

  const conversationT = useTranslation('conversations')

  // API
  const [sendConversationFeedback, sendConversationFeedbackResult] = useEditConversationMutation()
  const { refetch } = useGetConversationByIdQuery({ id: conversationId || '' })

  // Handlers for feedback fields
  const handleWhatIsWrongChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWhatIsWrong(e.target.value)
  }

  const handleWhyWrongChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWhyWrong(e.target.value)
  }

  const handleCorrectResponseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCorrectResponse(e.target.value)
  }

  const onSubmit = () => {
    console.log({
      rating,
      whatIsWrong,
      whyWrong,
      correctResponse,
    })
    if (rating && whatIsWrong && whyWrong && correctResponse) {
      sendConversationFeedback({
        id: conversationId,
        body: {
          conversationFeedback: {
            rating,
            whatIsWrong,
            whyWrong,
            correctResponse,
          },
        },
      })
    }
  }

  useEffect(() => {
    if (sendConversationFeedbackResult.isSuccess) {
      setWhatIsWrong('')
      setWhyWrong('')
      setCorrectResponse('')
      refetch()
    }
  }, [sendConversationFeedbackResult.isSuccess, refetch])

  return (
    <Form
      layout="vertical"
      className={isOpen ? 'conversation-text-feedback feedback-form-open' : 'conversation-text-feedback'}
      onFinish={onSubmit}
    >
      <div className="feedback-header">
        <h4 className="title" onClick={() => setIsOpen((v) => !v)}>
          {conversationT.t('conversation.conversationTextFeedback.title')}
          {isOpen ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </h4>
      </div>

      {isOpen && (
        <>
          <div className="rating-wrapper">
            <LikeOutlined
              className={rating === 'like' ? 'rating-icon selected' : 'rating-icon'}
              onClick={() => setRating('like')}
            />
            <DislikeOutlined
              className={rating === 'dislike' ? 'rating-icon selected' : 'rating-icon'}
              onClick={() => setRating('dislike')}
            />
          </div>

          <Form.Item label="What’s wrong with the LLM response?" required>
            <Input.TextArea
              placeholder="The LLM provided a generic answer about establishing rates through market research, cost analysis, and dynamic pricing, which is not specific or fully accurate for an NVOCC (Non-Vessel Operating Common Carrier). It failed to mention the industry-specific methods legally allowed, such as Negotiated Rate Arrangements (NRAs) or rate tariffs under FMC regulations."
              value={whatIsWrong}
              onChange={handleWhatIsWrongChange}
              rows={5}
              style={{
                marginBottom: '10px',
                padding: '10px',
                borderRadius: '5px',
                resize: 'vertical',
              }}
            />
          </Form.Item>

          <Form.Item label="Why is it wrong?" required>
            <Input.TextArea
              placeholder="As an NVOCC, the user is bound by specific regulatory frameworks (FMC regulations). The general advice about market research and cost analysis is irrelevant or secondary because NVOCCs must establish rates through NRAs or published rate tariffs. The model lacked awareness of the legal requirements for NVOCCs, leading to an inaccurate and potentially misleading response."
              value={whyWrong}
              onChange={handleWhyWrongChange}
              rows={5}
              style={{
                marginBottom: '10px',
                padding: '10px',
                borderRadius: '5px',
                resize: 'vertical',
              }}
            />
          </Form.Item>

          <Form.Item label="Correct response" required>
            <Input.TextArea
              placeholder="As an NVOCC, you are required to establish your rates through either a Negotiated Rate Arrangement (NRA) or a rate tariff filed with the Federal Maritime Commission (FMC).

Negotiated Rate Arrangement (NRA): A written agreement between you and your customer outlining specific negotiated rates for shipments.

Rate Tariff: A published tariff that is filed with the FMC, containing standard rates applicable to your services.

Both methods are compliant with FMC regulations. You should choose the one that aligns with your business model and customer needs."
              value={correctResponse}
              onChange={handleCorrectResponseChange}
              rows={5}
              style={{
                marginBottom: '10px',
                padding: '10px',
                borderRadius: '5px',
                resize: 'vertical',
              }}
            />
          </Form.Item>

          <AppPrimaryButton
            type="primary"
            htmlType="submit"
            className="submit-button"
            size="middle"
            disabled={!whatIsWrong || !whyWrong || !correctResponse || !rating}
            loading={sendConversationFeedbackResult.isLoading}
          >
            {conversationT.t('conversation.conversationTextFeedback.submit')}
          </AppPrimaryButton>
        </>
      )}
    </Form>
  )
}

export default ConversationTextFeedback
